import { reactive, computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useMutation } from '@tanstack/vue-query';

import { actualizeProducts } from '../api';
import type {
  SegmentWithPassengers,
  ActualizeFlightDelayProduct,
  ActualizeProductsResponse,
  FlightDelayProduct,
} from '../types';

interface FlightDelayFormState {
  segments: SegmentWithPassengers[];
  pricing: {
    actual: number;
    perPassenger: number;
  };
  isInitialActualized: boolean;
}

type ActualizedFlightDelayBooking = {
  id: string;
  price: number;
  segmentIdx: number;
};

export const useFlightDelayStore = defineStore('flight-delay', () => {
  const productIdsBySegment = ref<Record<number, string>>({});
  const actualProductIdsBySegment = ref<Record<number, string>>({});
  const offerId = ref('');
  const state = reactive<FlightDelayFormState>({
    segments: [],
    pricing: {
      actual: 0,
      perPassenger: 0,
    },
    isInitialActualized: false,
  });

  const {
    mutate: actualizeFlightDelay,
    isLoading: isActualizing,
    isError: isActualizeError,
  } = useMutation({
    mutationKey: ['actualizeFlightDelayProducts'],
    mutationFn(products: ActualizeFlightDelayProduct[]) {
      return actualizeProducts(products, offerId.value);
    },
  });

  const init = (
    newFlightDelayProducts: FlightDelayProduct[],
    newOfferId: string,
    initialSegments: SegmentWithPassengers[] = [],
    initialPricePerPassenger = 0,
    initialActualPrice = 0,
    initialActualized = false,
  ) => {
    productIdsBySegment.value = {};
    actualProductIdsBySegment.value = {};
    newFlightDelayProducts.forEach((product, index) => {
      const segIdx = product.$ctx?.segment_idx ?? index;
      productIdsBySegment.value[segIdx] = product.id;
      actualProductIdsBySegment.value[segIdx] = product.id;
    });
    offerId.value = newOfferId;
    state.segments = [...initialSegments];
    state.pricing.perPassenger = initialPricePerPassenger;
    state.pricing.actual = initialActualPrice;
    state.isInitialActualized = initialActualized;
  };

  const totalAmount = computed(() => {
    const selectedCount = state.segments
      .flatMap(s => s.passengers)
      .filter(p => p.isSelected).length;
    return selectedCount * state.pricing.perPassenger;
  });

  const hasError = computed(() => {
    return isActualizeError.value;
  });

  const canSave = computed(() => {
    return !hasError.value && totalAmount.value > 0 && !isActualizing.value;
  });

  const getSelectedPassengersCount = (): number => {
    return state.segments.flatMap(s => s.passengers).filter(p => p.isSelected)
      .length;
  };

  const updateSegmentPrices = (pricePerPassenger: number): void => {
    state.segments.forEach(segment => {
      segment.passengers.forEach(passenger => {
        passenger.productPriceAmount = pricePerPassenger.toString();
      });
    });
  };

  const createActualizePayload = (
    onlySelected = false,
  ): { payload: ActualizeFlightDelayProduct[]; segmentIndices: number[] } => {
    const payload: ActualizeFlightDelayProduct[] = [];
    const segmentIndices: number[] = [];

    for (const segment of state.segments) {
      const prodId = productIdsBySegment.value[segment.idx];
      if (!prodId) continue;

      if (onlySelected && !segment.passengers.some(p => p.isSelected)) continue;

      const passengers = onlySelected
        ? segment.passengers.filter(p => p.isSelected).map(p => p.idx)
        : segment.passengers
            .filter(p => p.isSelected && !!p.iin)
            .map(p => p.idx);

      payload.push({
        id: prodId,
        data: {
          segments: [{ idx: segment.idx, passengers }],
        },
      });
      segmentIndices.push(segment.idx);
    }

    return { payload, segmentIndices };
  };

  const handleActualizeSuccess = (
    res: ActualizeProductsResponse,
    segmentIndices: number[],
  ) => {
    if (!res.data?.length) return;

    let totalPrice = 0;
    res.data.forEach((item, i) => {
      const segIdx = segmentIndices[i];
      if (segIdx !== undefined) {
        actualProductIdsBySegment.value[segIdx] = item.id;
      }
      if (item.price?.amount) {
        totalPrice += item.price.amount;
      }
    });

    if (totalPrice > 0) {
      const countForCalculation = getSelectedPassengersCount();
      const pricePerPassengerValue = Math.round(
        countForCalculation > 0 ? totalPrice / countForCalculation : 0,
      );

      state.pricing.actual = totalPrice;
      state.pricing.perPassenger = pricePerPassengerValue;

      updateSegmentPrices(pricePerPassengerValue);
      state.isInitialActualized = true;
    }
  };

  const updatePassengerSelection = (
    segmentIdx: number,
    passengerIdx: number,
    isSelected: boolean,
  ) => {
    if (segmentIdx < 0 || segmentIdx >= state.segments.length) {
      return;
    }

    const segment = state.segments[segmentIdx];
    if (!segment?.passengers?.[passengerIdx]) {
      return;
    }

    state.segments[segmentIdx] = {
      ...segment,
      passengers: segment.passengers.map((passenger, idx) =>
        idx === passengerIdx ? { ...passenger, isSelected } : passenger,
      ),
    };
  };

  const resetSegmentAllPassengers = (segmentIdx: number) => {
    if (segmentIdx < 0 || segmentIdx >= state.segments.length) {
      return;
    }

    const segment = state.segments[segmentIdx];
    if (segment?.passengers) {
      state.segments[segmentIdx] = {
        ...segment,
        passengers: segment.passengers.map(passenger => ({
          ...passenger,
          isSelected: false,
        })),
      };
    }
  };

  const fetchInitialActualization =
    (): Promise<ActualizeProductsResponse | null> => {
      if (state.isInitialActualized) {
        return Promise.resolve(null);
      }

      const { payload, segmentIndices } = createActualizePayload(false);

      return new Promise(resolve => {
        actualizeFlightDelay(payload, {
          onSuccess: (res: ActualizeProductsResponse) => {
            handleActualizeSuccess(res, segmentIndices);
            resolve(res);
          },
          onError: () => {
            resolve(null);
          },
        });
      });
    };

  const actualizeProduct = (
    onSuccess?: (bookings: ActualizedFlightDelayBooking[]) => void,
  ): void => {
    const { payload, segmentIndices } = createActualizePayload(true);

    actualizeFlightDelay(payload, {
      onSuccess: (res: ActualizeProductsResponse) => {
        handleActualizeSuccess(res, segmentIndices);

        const bookings: ActualizedFlightDelayBooking[] = res.data.map(
          (item, i) => ({
            id: item.id || actualProductIdsBySegment.value[segmentIndices[i]],
            price: item.price?.amount || 0,
            segmentIdx: segmentIndices[i],
          }),
        );

        onSuccess?.(bookings);
      },
    });
  };

  const retryActualization = (): void => {
    if (!state.isInitialActualized) {
      fetchInitialActualization();
    } else {
      actualizeProduct();
    }
  };

  const updateSegments = (segments: SegmentWithPassengers[]) => {
    state.segments = [...segments];
  };

  return {
    state,
    isActualizing,
    hasError,
    totalAmount,
    canSave,
    init,
    updatePassengerSelection,
    resetSegmentAllPassengers,
    fetchInitialActualization,
    actualizeProduct,
    retryActualization,
    updateSegments,
  };
});
